import { useState, useCallback, useEffect } from 'react';
import { nightAuditService } from '../services/api';
import { toast } from 'react-toastify';

/**
 * Custom hook for managing Night Audit state and operations
 * Simplified 3-step system: Checkout -> Checkin -> Rollover
 */
export const useNightAudit = () => {
  const [audit, setAudit] = useState(null);
  const [stepInfo, setStepInfo] = useState(null);
  const [stepData, setStepData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load current audit data
   */
  const loadAudit = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await nightAuditService.getCurrentAudit();
      const data = response.data || response;
      
      console.log('Night Audit loaded:', data);
      
      setAudit(data.audit);
      setStepInfo(data.currentStepInfo);
      setStepData(data.stepData);
      setSteps(data.availableSteps || []);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to load audit';
      setError(message);
      console.error('Load audit error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh current step data
   */
  const refreshData = useCallback(async () => {
    if (!audit) return;
    
    setIsRefreshing(true);
    try {
      const response = await nightAuditService.refreshStepData();
      const data = response.data || response;
      
      setStepData(data.stepData);
      setStepInfo(data.stepInfo);
      
      toast.success('Data refreshed successfully');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to refresh data';
      setError(message);
      toast.error(message);
    } finally {
      setIsRefreshing(false);
    }
  }, [audit]);

  /**
   * Complete current step
   * @returns {Promise<boolean>} Success status
   */
  const completeStep = useCallback(async () => {
    if (!audit?.currentStep) {
      toast.error('No active step to complete');
      return false;
    }
    
    setIsCompleting(true);
    try {
      const response = await nightAuditService.completeStep(audit.currentStep);
      const data = response.data || response;
      
      console.log('Step completed:', data);
      
      // Show success message
      if (data.audit.status === 'COMPLETED') {
        toast.success('Night Audit completed successfully!');
      } else {
        toast.success('Step completed successfully');
      }
      
      // Reload to get next step
      await loadAudit();
      
      return true;
    } catch (err) {
      console.error('Complete step error:', err);
      
      // Check if it's a reset error from backend
      const resetInfo = err.response?.data?.details;
      if (resetInfo?.resetToStep) {
        // Step was auto-reset due to pending items
        const resetMessage = resetInfo.resetReason || 'Step was reset to incomplete status';
        toast.error(`${resetMessage}. Please complete pending items.`, {
          duration: 5000,
        });
        
        // Reload to show the reset step
        await loadAudit();
        
        return false;
      }
      
      // Regular error
      const message = err.response?.data?.error || 'Failed to complete step';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsCompleting(false);
    }
  }, [audit, loadAudit]);

  // Load on mount
  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  return {
    // State
    audit,
    stepInfo,
    stepData,
    steps,
    isLoading,
    isRefreshing,
    isCompleting,
    error,
    
    // Actions
    loadAudit,
    refreshData,
    completeStep,
  };
};
